<template>
  <section class="section small" v-if="domain">
    <div class="container">
      <ViewSelector :view="view" @change="setView($event)">
        <Title :skipMarginBottom="true">
          Computers
          <Plus
            v-if="!domain.permission || domain.permission >= 32"
            @click="showDownload = true"
          ></Plus>
        </Title>
      </ViewSelector>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="false"
        @submit="search($event)"
      ></SearchBar>

      <Tips
        v-if="firstLoadDone && $store.state.computer.computers.length !== 0"
        path="computers"
        xKey="dismissedTips"
        property="rightClick"
        :rightClickIconVisible="true"
        >Right-click on a computer name to open the context menu
      </Tips>

      <Tips
        v-if="firstLoadDone"
        path="computers"
        xKey="dismissedTips"
        property="computerNotification"
        >Be notified when a new computer is added to the domain -> go to profile
        settings
      </Tips>

      <p
        class="is-italic"
        v-if="firstLoadDone && $store.state.computer.computers.length === 0"
      >
        <i>No computers.</i>
      </p>

      <div v-if="firstLoadDone && $store.state.computer.computers.length !== 0">
        <View1
          v-if="view == 1"
          :items="filtered"
          @order="dispatchComputerOrder($event)"
          @save="rename($event.computer, $event.text)"
          @cancel="toggleRename($event)"
          @menu="$refs.menu.open($event.e, $event.computer)"
        ></View1>

        <View2
          v-if="view == 2"
          :items="filtered"
          @order="dispatchComputerOrder($event)"
          @save="rename($event.computer, $event.text)"
          @cancel="toggleRename($event)"
          @menu="$refs.menu.open($event.e, $event.computer)"
        ></View2>

        <Paging
          v-show="!searching && firstLoadDone"
          :items="paging.pages"
          :selected="paging.page"
          :size="paging.max"
          @change="setPage($event)"
          @size="setPageMax($event)"
          :hasOptions="true"
          autosave="computers"
        ></Paging>
      </div>
    </div>

    <vue-context ref="menu">
      <template v-if="child.data && domain" slot-scope="child">
        <li v-if="domain.permission < 4 && domain.permission">
          <a>
            <i>Too low permissions</i>
          </a>
        </li>
        <li v-if="domain.permission >= 16 || !domain.permission">
          <a @click.prevent="toggleRename(child.data)">Rename</a>
        </li>

        <li v-if="domain.permission >= 4 || !domain.permission">
          <router-link
            :to="
              `/domains/${$route.params.domainUID}/computers/${child.data.uid}/applications`
            "
            >Applications</router-link
          >
        </li>

        <Wol
          v-if="domain.permission >= 8 || !domain.permission"
          :computers="[child.data]"
          >Wake computer</Wol
        >

        <li v-if="domain.permission >= 64 || !domain.permission">
          <a
            @click.prevent="
              selected = child.data;
              showPowershell = true;
            "
            >Powershell</a
          >
        </li>

        <li
          v-if="
            $store.state.settings.server.licenseEnabled &&
              (domain.permission >= 64 || !domain.permission)
          "
        >
          <a
            @click.prevent="
              selected = child.data;
              showComputerLicense = true;
            "
            >Manage license</a
          >
        </li>

        <li v-if="domain.permission >= 64 || !domain.permission">
          <a
            @click.prevent="
              selected = child.data;
              showDelete = true;
            "
            >Delete</a
          >
        </li>
      </template>
    </vue-context>

    <Msi v-if="showDownload" :domain="domain" v-model="showDownload"></Msi>

    <ComputerConfirm
      v-if="selected && showDelete"
      v-model="showDelete"
      :computer="selected"
      @hide="selected = null"
      @confirmed="_delete(selected)"
    ></ComputerConfirm>

    <Powershell
      v-if="selected && showPowershell"
      v-model="showPowershell"
      :computer="selected"
    ></Powershell>

    <License
      v-if="selected && showComputerLicense"
      v-model="showComputerLicense"
      :computer="selected"
    ></License>
  </section>
</template>
<script>
import { postApi } from "@/utils/http";
import { disconnectComputers } from "@/utils/socket";
import { getId } from "@/utils/document";
import { dispatchComputerOrder } from "@/utils/order";

import { getStartIndex, getNumbers } from "@/utils/paging";

export default {
  components: {
    Tips: () => import("@/components/main/Tips.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    VueContext: () => import("vue-context"),
    ComputerConfirm: () => import("@/components/main/computer/Delete.vue"),
    Powershell: () => import("@/components/main/computer/Powershell.vue"),
    License: () => import("@/components/main/computer/License.vue"),
    Wol: () => import("@/components/main/context_menu/Wol.vue"),
    Plus: () => import("@/components/main/Plus.vue"),
    Msi: () => import("@/components/main/domain/Msi.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    ViewSelector: () => import("@/components/main/ViewSelector.vue"),
    View1: () => import("@/components/main/computer/View1.vue"),
    View2: () => import("@/components/main/computer/View2.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue")
  },
  props: { domain: Object },
  computed: {
    view() {
      return this.$store.state.settings.user.computers &&
        this.$store.state.settings.user.computers.view
        ? this.$store.state.settings.user.computers.view
        : 1;
    },
    filtered() {
      let items = this.$store.state.computer.computers;

      if (this.filter && this.filter !== "") {
        items = items.filter(
          x =>
            (x.name && x.name.toLowerCase().indexOf(this.filter) !== -1) ||
            (x.friendlyName &&
              x.friendlyName.toLowerCase().indexOf(this.filter) !== -1)
        );
      }

      this.setPages(items);

      if (this.paging.max === -1) return items;
      else {
        return items.slice(
          this.paging.start,
          this.paging.page * this.paging.max
        );
      }
    }
  },
  data() {
    return {
      showComputerLicense: false,
      showPowershell: false,
      selected: null,
      showDelete: false,
      showGuide: false,
      showDownload: false,
      saving: false,
      firstLoadDone: false,
      searching: false,
      tempFilter: "",
      filter: "",
      searchId: getId(),
      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: []
      }
    };
  },
  async created() {
    try {
      await this.$store.dispatch("settings/getUserSetting", {
        path: "computers",
        key: "paging"
      });

      const paging = this.$store.state.settings.user.computers?.paging;
      if (paging && paging.max) this.paging.max = paging.max;
      this.setPage(1);

      await this.$store.dispatch("computer/fetch", {
        domainUID: this.$route.params.domainUID
      });

      this.firstLoadDone = true;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get computers.", "danger", 5000);
    }
  },
  beforeDestroy() {},
  methods: {
    setPageMax(size) {
      this.paging.max = size;

      this.setPage(1);
      this.setPages(this.$store.state.computer.computers);
    },
    setPages(items) {
      this.paging.pages = getNumbers(items, this.paging.max);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    setView(view) {
      this.$store.dispatch("settings/setUser", {
        path: "computers",
        key: "view",
        value: view
      });
    },
    dispatchComputerOrder: dispatchComputerOrder,
    search(searchStr) {
      this.searching = true;

      searchStr = searchStr.trim().toLowerCase();
      this.filter = searchStr;

      this.setPage(1);

      this.searching = false;
    },
    toggleRename(computer) {
      this.$store.commit("computer/editComputer", {
        c: computer,
        obj: { edit: !computer.edit }
      });
    },
    async _delete(computer) {
      await this.$helpers.wait(1);

      disconnectComputers([computer.uid]);

      await this.$helpers.wait(1);

      await this.$store.dispatch("computer/delete", {
        computerUID: computer.uid,
        domainUID: this.domain.uid
      });

      this.showDelete = false;
    },

    async rename(computer, text) {
      this.saving = true;
      text = text.trim();

      await postApi("set/computerFriendlyName", {
        uid: computer.uid,
        friendlyName: text,
        domainUID: this.domain.uid
      });

      this.$store.commit("computer/editComputer", {
        c: computer,
        obj: { friendlyName: text, edit: false }
      });

      this.saving = false;
    }
  }
};
</script>
